import LoanDetailCalculation from './shared/housingLoanDetailCalculation';
import { Utils } from './shared/loanUtils';
import { NumberFieldWithRange } from './shared/numberFieldWithRange';
import { CalculatorHooksExtended, LoanRates } from './types';

class HousingCalculatorGeneric {
    private root: HTMLElement;

    private data!: LoanRates;

    private fields: Map<string, NumberFieldWithRange> = new Map();

    constructor(private hooks: CalculatorHooksExtended) {
        this.root = document.getElementById(hooks.main_holder) as HTMLElement;
        if (!this.root) return;

        try {
            this.data = JSON.parse(this.root.getAttribute('data-rates') ?? '{}');
        } catch {
            return;
        }

        this.initFields();
        this.calculate();
    }

    private initFields(): void {
        ['PropertyValue', 'LoanAmount', 'LoanTerm'].forEach((key) => {
            const hook = this.hooks.inputs[key as keyof typeof this.hooks.inputs];
            const field = new NumberFieldWithRange(this.root, hook, () => this.calculate());
            this.fields.set(key, field);
        });
    }

    private calculate(): void {
        const propVal = this.fields.get('PropertyValue')!.getRawValue();
        const loanAmt = this.fields.get('LoanAmount')!.getRawValue();
        const term = this.fields.get('LoanTerm')!.getRawValue();

        const allValid = Array.from(this.fields.values()).every((f) => f.validate());
        Utils.toggleDisplay(this.root, this.hooks.resultHolders.error, !allValid);
        Utils.toggleDisplay(this.root, this.hooks.result.ratio_container, allValid);

        if (!allValid) {
            Utils.toggleDisplay(this.root, this.hooks.resultHolders.success, false);
            Utils.toggleDisplay(this.root, this.hooks.resultHolders.reject, false);
            return;
        }

        const ltv = (loanAmt / propVal) * 100;
        const nominalRate = this.getRate(ltv);
        const maxLtv = this.getMaxLtv();
        const isRejected = ltv > maxLtv || nominalRate <= 0;

        Utils.toggleDisplay(this.root, this.hooks.resultHolders.reject, isRejected);
        Utils.toggleDisplay(this.root, this.hooks.resultHolders.success, !isRejected);
        Utils.toggleClass(this.root, this.hooks.result.ratio_container, 'errored', isRejected);
        Utils.setInnerText(this.root, this.hooks.result.loan_ratio, Utils.formatPercent(ltv));

        if (isRejected) {
            Utils.setInnerText(this.root, this.hooks.result.loan_ratio_err, `${maxLtv} %`);
        } else {
            this.renderSuccess(loanAmt, nominalRate, term);
        }
    }

    private renderSuccess(loan: number, rate: number, years: number): void {
        const monthlyInstallmentFee =
            parseFloat((document.getElementById(this.hooks.inputs.MonthlyFee) as HTMLInputElement)?.value) ||
            this.data.MonthlyServiceFee ||
            0;
        const establishmentFee =
            parseFloat((document.getElementById(this.hooks.inputs.EstablishmentFee) as HTMLInputElement)?.value) ||
            this.data.EstablishmentFee ||
            0;

        const loanCalculation = new LoanDetailCalculation(
            loan,
            rate,
            years,
            establishmentFee,
            monthlyInstallmentFee,
        ).getDetails();
        const monthlyTotal = loanCalculation.installmentAmountInclFee;
        const effectiveRate = loanCalculation.effectiveInterestRate;

        Utils.setInnerText(this.root, this.hooks.result.monthly_cost, Utils.formatCurrency(monthlyTotal));
        Utils.setInnerText(this.root, this.hooks.result.rate_nominal, Utils.formatPercent(rate));
        Utils.setInnerText(this.root, this.hooks.result.rate_effective, Utils.formatPercent(effectiveRate));
    }

    private getRate(ltv: number): number {
        const ranges = this.data.InterestIntervallList || [];
        for (const rangeObj of ranges) {
            const key = Object.keys(rangeObj)[0];
            const [start, end] = key.split('_').map(Number);
            if (ltv >= start && ltv <= end) return rangeObj[key];
        }
        return 0;
    }

    private getMaxLtv(): number {
        const valid = (this.data.InterestIntervallList || []).filter((r: object) => Object.values(r)[0] > 0);
        return valid.length ? Math.max(...valid.map((r: object) => Number(Object.keys(r)[0].split('_')[1]))) : 0;
    }
}
export default function initializeLoanCalc(hooks: CalculatorHooksExtended) {
    return new HousingCalculatorGeneric(hooks);
}
